export interface AudioSettings {
  master: number;
  music: number;
  effects: number;
  muted: boolean;
}

export type SalvageSound =
  | 'ui'
  | 'scan'
  | 'cut-valid'
  | 'cut-invalid'
  | 'grapple-fire'
  | 'grapple-attach'
  | 'grapple-creak'
  | 'grapple-break'
  | 'deposit'
  | 'impact'
  | 'pressure'
  | 'event-warning'
  | 'success'
  | 'wreck-break';

const DEFAULT_AUDIO: AudioSettings = {
  master: 0.78,
  music: 0.64,
  effects: 0.76,
  muted: false,
};

const SOUND_SHAPES: Record<
  SalvageSound,
  readonly [startHz: number, endHz: number, duration: number, level: number]
> = {
  ui: [640, 520, 0.055, 0.12],
  scan: [360, 1280, 0.38, 0.18],
  'cut-valid': [980, 740, 0.11, 0.15],
  'cut-invalid': [170, 110, 0.14, 0.18],
  'grapple-fire': [520, 180, 0.12, 0.18],
  'grapple-attach': [150, 240, 0.18, 0.22],
  'grapple-creak': [95, 72, 0.42, 0.2],
  'grapple-break': [320, 54, 0.32, 0.28],
  deposit: [190, 760, 0.42, 0.3],
  impact: [120, 62, 0.2, 0.24],
  pressure: [680, 90, 0.72, 0.24],
  'event-warning': [220, 170, 0.5, 0.26],
  success: [330, 880, 0.82, 0.28],
  'wreck-break': [82, 38, 1.2, 0.38],
};

function clampUnit(value: number): number {
  return Math.min(1, Math.max(0, Number.isFinite(value) ? value : 0));
}

export function normalizeAudioSettings(
  settings: Partial<AudioSettings> = DEFAULT_AUDIO,
): AudioSettings {
  return {
    master: clampUnit(settings.master ?? DEFAULT_AUDIO.master),
    music: clampUnit(settings.music ?? DEFAULT_AUDIO.music),
    effects: clampUnit(settings.effects ?? DEFAULT_AUDIO.effects),
    muted: settings.muted ?? DEFAULT_AUDIO.muted,
  };
}

export class AudioDirector {
  private context: AudioContext | null = null;
  private master: GainNode | null = null;
  private effects: GainNode | null = null;
  private tracks: Record<'exploration' | 'tension' | 'emergency', HTMLAudioElement> | null = null;
  private musicLevels = { exploration: 0, tension: 0, emergency: 0 };
  private targetMusicLevels = { exploration: 0.58, tension: 0, emergency: 0 };
  private fadeTimer: number | null = null;
  private intensity = 0;
  private settings = DEFAULT_AUDIO;

  async unlock(): Promise<boolean> {
    if (this.context) {
      if (this.context.state === 'suspended') await this.context.resume();
      return this.context.state === 'running';
    }
    if (typeof window === 'undefined') return false;
    this.context = new AudioContext();
    this.buildMix(this.context);
    this.buildMusic();
    if (this.context.state === 'suspended') await this.context.resume();
    if (this.tracks) {
      await Promise.allSettled(Object.values(this.tracks).map((track) => track.play()));
    }
    return this.context.state === 'running';
  }

  configure(settings: Partial<AudioSettings>): void {
    this.settings = normalizeAudioSettings({ ...this.settings, ...settings });
    this.applyMix();
  }

  setIntensity(instability: number, emergency = false): void {
    this.intensity = clampUnit(instability / 100);
    const tensionMix = Math.max(0, (this.intensity - 0.28) / 0.57);
    const emergencyMix = emergency || this.intensity >= 0.85 ? 1 : 0;
    this.targetMusicLevels = {
      exploration: emergencyMix ? 0.08 : 0.58 - tensionMix * 0.32,
      tension: emergencyMix ? 0.12 : tensionMix * 0.52,
      emergency: emergencyMix * 0.68,
    };
  }

  play(sound: SalvageSound): void {
    if (!this.context || !this.effects || this.settings.muted) return;
    const [startHz, endHz, duration, level] = SOUND_SHAPES[sound];
    const now = this.context.currentTime;
    const oscillator = this.context.createOscillator();
    const gain = this.context.createGain();
    const filter = this.context.createBiquadFilter();
    oscillator.type = sound === 'impact' || sound === 'wreck-break' ? 'sawtooth' : 'triangle';
    oscillator.frequency.setValueAtTime(startHz, now);
    oscillator.frequency.exponentialRampToValueAtTime(Math.max(20, endHz), now + duration);
    filter.type = 'lowpass';
    filter.frequency.value = sound === 'pressure' ? 1100 : 2200;
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(level, now + 0.008);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + duration);
    oscillator.connect(filter).connect(gain).connect(this.effects);
    oscillator.start(now);
    oscillator.stop(now + duration + 0.03);
  }

  async suspend(): Promise<void> {
    if (this.context?.state === 'running') await this.context.suspend();
  }

  async resume(): Promise<void> {
    if (this.context?.state === 'suspended') await this.context.resume();
  }

  async dispose(): Promise<void> {
    if (this.fadeTimer !== null) {
      window.clearInterval(this.fadeTimer);
      this.fadeTimer = null;
    }
    if (this.tracks) {
      for (const track of Object.values(this.tracks)) {
        track.pause();
        track.currentTime = 0;
      }
      this.tracks = null;
    }
    if (this.context) await this.context.close();
    this.context = null;
  }

  private buildMix(context: AudioContext): void {
    const compressor = context.createDynamicsCompressor();
    compressor.threshold.value = -18;
    compressor.ratio.value = 4;
    compressor.connect(context.destination);

    this.master = context.createGain();
    this.effects = context.createGain();
    this.master.connect(compressor);
    this.effects.connect(this.master);
    this.applyMix();
  }

  private buildMusic(): void {
    const createTrack = (src: string): HTMLAudioElement => {
      const track = new Audio(src);
      track.loop = true;
      track.preload = 'auto';
      track.crossOrigin = 'anonymous';
      track.volume = 0;
      return track;
    };
    this.tracks = {
      exploration: createTrack('/audio/exploration.mp3'),
      tension: createTrack('/audio/tension.mp3'),
      emergency: createTrack('/audio/emergency.mp3'),
    };
    this.fadeTimer = window.setInterval(() => {
      this.fadeMusic();
    }, 60);
    this.fadeMusic();
  }

  private fadeMusic(): void {
    if (!this.tracks) return;
    const musicGain = this.settings.muted ? 0 : this.settings.master * this.settings.music;
    for (const key of ['exploration', 'tension', 'emergency'] as const) {
      const next =
        this.musicLevels[key] + (this.targetMusicLevels[key] - this.musicLevels[key]) * 0.12;
      this.musicLevels[key] =
        Math.abs(next - this.targetMusicLevels[key]) < 0.002 ? this.targetMusicLevels[key] : next;
      this.tracks[key].volume = clampUnit(this.musicLevels[key] * musicGain);
    }
  }

  private applyMix(): void {
    if (!this.context || !this.master || !this.effects) {
      this.fadeMusic();
      return;
    }
    const now = this.context.currentTime;
    this.master.gain.setTargetAtTime(this.settings.muted ? 0 : this.settings.master, now, 0.04);
    this.effects.gain.setTargetAtTime(this.settings.effects, now, 0.025);
    this.fadeMusic();
  }
}

export const audioDirector = new AudioDirector();
